import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { getHomeSeoContent } from '../content/homeSeoContent.mjs';

type Segment = string | { text: string; href: string };

interface HomeSeoContentProps {
  lang: 'en' | 'fr';
}

const INLINE_LINK = 'text-[#094426] font-semibold underline underline-offset-2 hover:text-[#07361e]';
const CTA_LINK = 'inline-flex items-center gap-1.5 text-sm font-bold text-[#094426] hover:underline';

const renderSegments = (segments: Segment[]) =>
  segments.map((seg, i) =>
    typeof seg === 'string' ? (
      <React.Fragment key={i}>{seg}</React.Fragment>
    ) : (
      <Link key={i} to={seg.href} className={INLINE_LINK}>
        {seg.text}
      </Link>
    )
  );

/**
 * Indexable editorial block presenting the Cameroon Land & Property Advisory Guide.
 * Copy lives in src/content/homeSeoContent.mjs and is shared with the static
 * pre-renderer (renderHomeSeoContentHtml) so both outputs stay identical.
 */
export const HomeSeoContent: React.FC<HomeSeoContentProps> = ({ lang }) => {
  const { sections } = getHomeSeoContent(lang);
  const isFr = lang === 'fr';
  const [openIds, setOpenIds] = useState<Record<string, boolean>>(() =>
    sections.length > 0 ? { [sections[0].id]: true } : {}
  );

  const toggleSection = (id: string) => {
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-xs">
        {/* Section Header */}
        <div className="max-w-3xl mb-6 sm:mb-8">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#094426] mb-2">
            <span className="w-2 h-2 rounded-full bg-[#094426]" />
            <span>{isFr ? 'Sécurité Juridique & Cadastre' : 'Legal Framework & Land Registry'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-900 tracking-tight">
            {isFr
              ? 'Guide Foncier et Pratiques Notariales au Cameroun'
              : 'Cameroon Real Estate & Notary Legal Guide'}
          </h2>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed mt-1">
            {isFr
              ? 'Procédures MINDCAF, vérification du Titre Foncier, bornage contradictoire et sécurité des transactions sur les 10 régions.'
              : 'Official MINDCAF procedures, land title deed verification, boundary surveys, and safe transaction guidelines across all 10 regions.'}
          </p>
        </div>

        {/* 2-Column Responsive Grid of Editorial Cards (Accordion on mobile, full grid on desktop) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8">
          {sections.map((s) => {
            const isOpen = Boolean(openIds[s.id]);
            return (
              <div
                key={s.id}
                id={`home-${s.id}`}
                className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-5 sm:p-6 flex flex-col justify-between hover:border-emerald-200 transition-colors"
              >
                <div>
                  <button
                    type="button"
                    onClick={() => toggleSection(s.id)}
                    aria-expanded={isOpen}
                    className="w-full text-left flex items-center justify-between gap-3 lg:cursor-default"
                  >
                    <h3
                      id={`home-${s.id}-heading`}
                      className="text-base sm:text-xl font-serif font-bold text-slate-900 tracking-tight flex items-center gap-2"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#094426] shrink-0" />
                      <span>{s.heading}</span>
                    </h3>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-500 shrink-0 transition-transform lg:hidden ${
                        isOpen ? 'rotate-180 text-[#094426]' : ''
                      }`}
                    />
                  </button>

                  <div className={`${isOpen ? 'block' : 'hidden'} lg:block space-y-3 mt-3`}>
                    {s.paragraphs.map((p: Segment[], i: number) => (
                      <p key={i} className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                        {renderSegments(p)}
                      </p>
                    ))}
                    {(s.subsections || []).map((sub) => (
                      <div key={sub.heading} className="pt-2 border-t border-slate-200/60 mt-2">
                        <h4 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-1.5">
                          <span className="text-emerald-700">✓</span>
                          <span>{sub.heading}</span>
                        </h4>
                        {sub.paragraphs.map((p: Segment[], i: number) => (
                          <p key={i} className="mt-1 text-slate-600 text-xs sm:text-sm leading-relaxed">
                            {renderSegments(p)}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                {(s.links || []).length > 0 && (
                  <div className={`${isOpen ? 'block' : 'hidden'} lg:block pt-3 mt-4 border-t border-slate-200/60`}>
                    <ul className="space-y-1.5">
                      {s.links!.map((l) => (
                        <li key={l.href}>
                          <Link to={l.href} className={CTA_LINK}>
                            <span>{l.text}</span>
                            <span>→</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

